// CRM count tiles for the main dashboard, fed by the same endpoint as the CRM
// dashboard (pages/CrmDashboard), for this month.
// Nothing is fetched and no tiles are returned when `enabled` is false.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  UserAddOutlined,
  FundOutlined,
  DollarOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import { getCrmDashboardApi } from "../../../apiservice/crm";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { formatAmountCompact } from "../../../lib/currency";
import { TILE } from "../dashboardTheme";

const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? "" : "s"}`;

const useCrmTiles = (enabled) => {
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency || "AED";
  const country = authInfo?.country;
  const [kpis, setKpis] = useState({});

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    getCrmDashboardApi({ range: "month", kpis_only: true })
      .then((res) => {
        if (!cancelled) setKpis(res?.data?.kpis || {});
      })
      .catch((err) => console.error("Failed to load CRM summary", err));
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  if (!enabled) return [];

  const amount = (v) => formatAmountCompact({ value: v, country, decimals: 1 });
  const full = (v) => `${Number(v || 0).toLocaleString()} ${currency}`;
  const overdue = Number(kpis.overdue_tasks || 0);

  return [
    {
      key: "leads",
      title: "New Leads",
      icon: <UserAddOutlined />,
      accent: TILE.leads,
      value: Number(kpis.new_in_range || 0).toLocaleString(),
      sub: `this month · ${kpis.new_today || 0} today`,
      to: "/crm/leads",
    },
    {
      key: "pipeline",
      title: "Open Pipeline",
      icon: <FundOutlined />,
      accent: TILE.pipeline,
      value: amount(kpis.pipeline_value),
      unit: currency,
      hint: full(kpis.pipeline_value),
      sub: plural(kpis.open_deals || 0, "open deal"),
      to: "/crm/deals",
    },
    {
      key: "revenue",
      title: "Won Revenue",
      icon: <DollarOutlined />,
      accent: TILE.profit,
      value: amount(kpis.revenue_in_range),
      unit: currency,
      hint: full(kpis.revenue_in_range),
      sub: `this month · ${plural(kpis.won_in_range || 0, "deal")} won`,
      to: "/crm/deals",
    },
    {
      key: "tasks",
      title: "Overdue Tasks",
      icon: <ScheduleOutlined />,
      accent: overdue > 0 ? TILE.overdue : TILE.calm,
      value: overdue.toLocaleString(),
      danger: overdue > 0,
      sub: `${kpis.tasks_today || 0} due today · ${kpis.upcoming_tasks || 0} upcoming`,
      to: "/crm/tasks",
    },
  ];
};

export default useCrmTiles;
